import * as Yup from 'yup';
import {getSelectItemLabel, ISelectItem} from '~/components/Controls/Select';
import {DateRange, stringDateFormat} from '~/lib/date';
import {IDSchema, IModel} from '~/lib/model';

export enum PaymentStatus {
    ALL = 'all',
    RECEIVED = 'received',
    PAID = 'paid',
    REJECTED = 'rejected'
}

export const paymentStatuses: ISelectItem<PaymentStatus>[] = [
    {value: PaymentStatus.RECEIVED, label: 'Pending'},
    {value: PaymentStatus.PAID, label: 'Completed'},
    {value: PaymentStatus.REJECTED, label: 'Rejected'}
];

const intervalDateFormat: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
};

export const getPaymentStatusLabel = (paymentStatus: PaymentStatus) => getSelectItemLabel(paymentStatuses, paymentStatus);

export interface Interval {
    start: string;
    end: string;
}

export const formatInterval = (i: Interval) =>
    `${stringDateFormat(i.start, intervalDateFormat)} - ${stringDateFormat(i.end, intervalDateFormat)}`;

export interface IPayment extends IModel {
    accountId: number;
    sellerName: string;
    amount: number;
    status: PaymentStatus;
    issuedAt: string;
    completedAt?: string;
    updatedAt?: number;
    revenuePeriod?: Interval;
    rejectionReason?: string;
}

export interface Payment extends Omit<IPayment, 'revenuePeriod'> {
    revenuePeriod: DateRange;
}

export const DateRangeSchema = Yup.object().shape<DateRange>({
    startDate: Yup.date().nullable(),
    endDate: Yup.date().nullable(),
});

export const PaymentRequestSchema = Yup.object().shape<Payment>({
    id: null,
    accountId: IDSchema.required(),
    amount: Yup.number().positive('can\'t be less or equal zero'),
    status: null,
    issuedAt: null,
    completedAt: null,
    sellerName: null,
    updatedAt: null,
    rejectionReason: null,
    revenuePeriod: DateRangeSchema.required()
});

export const defaultPayment: IPayment = {
    id: null,
    accountId: null,
    amount: null,
    status: null,
    issuedAt: null,
    completedAt: null,
    sellerName: null,
    updatedAt: null,
    rejectionReason: null,
    revenuePeriod: null
};

export interface IPaymentTransactionHistory {
    accountId: number;
    sellerName: string,
    net?: string | null;
    type: string;
    amount: number;
    comment?: string;
    createdAt: string;
    revenuePeriod: {
        end: string;
        start: string;
    }
}

export enum TransactionType {
    MANUAL = 'Manual',
    AUTO = 'Automatic',
}

export const transactionTypes: ISelectItem<TransactionType>[] = [
    {value: TransactionType.MANUAL, label: TransactionType.MANUAL},
    {value: TransactionType.AUTO, label: TransactionType.AUTO},
];
